import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import equivalenciasService from '../../services/equivalenciasProveedor.service';
import proveedoresService from '../../services/proveedores.service';
import productosService from '../../services/productos.service';

const ETIQUETA = {
  confirmada: 'Confirmada',
  pendiente: 'Pendiente',
  rechazada: 'Rechazada',
  misma_familia_distinto_producto: 'Misma familia, otro producto'
};

const campo = 'nexo-field';

const vacio = {
  proveedor_id: '',
  codigo_externo: '',
  nombre_comercial: '',
  producto_id: '',
  familia: '',
  marca: '',
  calidad: '',
  unidad: '',
  presentacion: '',
  evidencia: ''
};

export default function EquivalenciasProveedor() {
  const { hasPermission } = useAuth();
  const puedeDecidir = typeof hasPermission === 'function' && hasPermission('productos', 'editar') === true;
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState(vacio);
  const [proveedores, setProveedores] = useState([]);
  const [productos, setProductos] = useState([]);
  const [relaciones, setRelaciones] = useState([]);
  const [propuesta, setPropuesta] = useState(null);
  const [errorCarga, setErrorCarga] = useState('');
  const [avisoCatalogo, setAvisoCatalogo] = useState('');

  const avisarDecision = (resultado, textoOk) => {
    if (resultado?.auditoria?.registrada === false) {
      toast.warning(resultado.auditoria.mensaje || 'La decisión se guardó, pero no quedó registro de auditoría.');
      return;
    }
    if (resultado?.auditoria?.registrada === true) {
      toast.success(`${textoOk} Quedó registrada en auditoría.`);
      return;
    }
    toast.success(textoOk);
  };

  const cargar = useCallback(async () => {
    setCargando(true);
    setErrorCarga('');
    setAvisoCatalogo('');
    const [listaRes, provRes, prodRes] = await Promise.allSettled([
      equivalenciasService.listar(),
      proveedoresService.obtenerTodos(),
      productosService.obtenerTodos()
    ]);
    if (listaRes.status === 'fulfilled') {
      setRelaciones(Array.isArray(listaRes.value) ? listaRes.value : []);
    } else {
      setRelaciones([]);
      const mensaje = listaRes.reason?.message || 'No se pudieron leer las relaciones';
      setErrorCarga(mensaje);
      toast.error(mensaje);
    }
    const avisos = [];
    if (provRes.status === 'fulfilled') {
      setProveedores(Array.isArray(provRes.value) ? provRes.value : []);
    } else {
      setProveedores([]);
      avisos.push(provRes.reason?.message || 'No se pudieron leer los proveedores');
    }
    if (prodRes.status === 'fulfilled') {
      setProductos(Array.isArray(prodRes.value) ? prodRes.value : []);
    } else {
      setProductos([]);
      avisos.push(prodRes.reason?.message || 'No se pudieron leer los productos');
    }
    setAvisoCatalogo(avisos.join(' '));
    setCargando(false);
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cambiar = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const verCandidatos = async () => {
    try {
      const data = await equivalenciasService.proponer(form);
      setPropuesta(data);
    } catch (error) {
      toast.error(error.message || 'No se pudo proponer');
    }
  };

  const registrar = async (estado) => {
    setGuardando(true);
    try {
      const resultado = await equivalenciasService.crear({ ...form, estado, origen: 'humano' });
      avisarDecision(resultado, estado === 'pendiente' ? 'Quedó pendiente de revisión' : 'Decisión registrada');
      setForm(vacio);
      setPropuesta(null);
      await cargar();
    } catch (error) {
      toast.error(error.message || 'No se pudo registrar');
    } finally {
      setGuardando(false);
    }
  };

  const decidir = async (relacion, estado) => {
    setGuardando(true);
    try {
      const resultado = await equivalenciasService.decidir(relacion.id, {
        estado,
        producto_id: relacion.producto_id || '',
        familia: relacion.familia || '',
        evidencia: relacion.evidenciaBorrador || relacion.evidencia || 'Revisión en pantalla'
      });
      avisarDecision(resultado, 'Decisión guardada');
      await cargar();
    } catch (error) {
      toast.error(error.message || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const actualizarFila = (id, patch) => {
    setRelaciones((prev) => prev.map((relacion) => (
      relacion.id === id ? { ...relacion, ...patch } : relacion
    )));
  };

  const nombreProveedor = (id) => proveedores.find((p) => p.id === id)?.nombre || id || '—';
  const nombreProducto = (id) => productos.find((p) => p.id === id)?.nombre || (id ? id : 'Sin producto');

  if (cargando) {
    return <div className="flex justify-center py-16"><Spinner /></div>;
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-1">
      <div>
        <h1 className="text-2xl font-bold text-e0e3-foreground">Equivalencias de proveedores</h1>
        <p className="mt-1 text-sm text-e0e3-muted">
          Relacioná el código de cada proveedor con un producto interno. Un nombre parecido no confirma la equivalencia.
        </p>
      </div>

      <section className="space-y-3 rounded-2xl border border-white/10 bg-e0e3-surface p-4">
        <h2 className="text-base font-semibold text-e0e3-foreground">Nueva relación</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm text-e0e3-muted">
            Proveedor
            <select className={campo} name="proveedor_id" value={form.proveedor_id} onChange={cambiar}>
              <option value="">Elegí un proveedor</option>
              {proveedores.map((prov) => (
                <option key={prov.id} value={prov.id}>{prov.nombre}</option>
              ))}
            </select>
          </label>
          <label className="text-sm text-e0e3-muted">
            Código del proveedor
            <input className={campo} name="codigo_externo" value={form.codigo_externo} onChange={cambiar} />
          </label>
          <label className="text-sm text-e0e3-muted sm:col-span-2">
            Nombre en la lista
            <input className={campo} name="nombre_comercial" value={form.nombre_comercial} onChange={cambiar} />
          </label>
          <label className="text-sm text-e0e3-muted">
            Producto interno
            <select className={campo} name="producto_id" value={form.producto_id} onChange={cambiar}>
              <option value="">Todavía sin producto</option>
              {productos.map((prod) => (
                <option key={prod.id} value={prod.id}>{prod.nombre}</option>
              ))}
            </select>
          </label>
          <label className="text-sm text-e0e3-muted">
            Familia
            <input className={campo} name="familia" value={form.familia} onChange={cambiar} placeholder="Se sugiere al ver candidatos" />
          </label>
          <label className="text-sm text-e0e3-muted">
            Marca
            <input className={campo} name="marca" value={form.marca} onChange={cambiar} />
          </label>
          <label className="text-sm text-e0e3-muted">
            Calidad o variante
            <input className={campo} name="calidad" value={form.calidad} onChange={cambiar} />
          </label>
          <label className="text-sm text-e0e3-muted">
            Unidad
            <input className={campo} name="unidad" value={form.unidad} onChange={cambiar} />
          </label>
          <label className="text-sm text-e0e3-muted">
            Presentación
            <input className={campo} name="presentacion" value={form.presentacion} onChange={cambiar} />
          </label>
          <label className="text-sm text-e0e3-muted sm:col-span-2">
            Evidencia
            <input className={campo} name="evidencia" value={form.evidencia} onChange={cambiar} />
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" color="secondary" onClick={verCandidatos}>Ver candidatos</Button>
          {puedeDecidir && (
            <>
              <Button type="button" color="secondary" disabled={guardando} onClick={() => registrar('pendiente')}>Dejar pendiente</Button>
              <Button type="button" color="primary" disabled={guardando} onClick={() => registrar('confirmada')}>Confirmar</Button>
              <Button type="button" color="danger" disabled={guardando} onClick={() => registrar('rechazada')}>Rechazar</Button>
              <Button type="button" color="warning" disabled={guardando} onClick={() => registrar('misma_familia_distinto_producto')}>
                Misma familia, otro producto
              </Button>
            </>
          )}
        </div>
        {propuesta && (
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3 text-sm text-e0e3-foreground">
            <p>{propuesta.mensaje}</p>
            <p className="mt-1 text-e0e3-muted">
              Familia: {propuesta.familia || '—'} · Calidad: {propuesta.calidad || '—'} · Estado: {ETIQUETA[propuesta.estado] || propuesta.estado}
            </p>
            {propuesta.candidatos?.length > 0 && (
              <ul className="mt-2 space-y-1">
                {propuesta.candidatos.map((cand) => (
                  <li key={cand.producto_id}>
                    <button
                      type="button"
                      className="text-left text-e0e3-accent"
                      onClick={() => setForm((prev) => ({ ...prev, producto_id: cand.producto_id, familia: propuesta.familia || prev.familia, calidad: propuesta.calidad || prev.calidad }))}
                    >
                      {cand.nombre}
                    </button>
                    <span className="text-e0e3-muted"> — {cand.motivo}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {avisoCatalogo && (
        <p className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-e0e3-muted">
          {avisoCatalogo} Las relaciones de esta empresa se muestran igual.
        </p>
      )}

      <section className="space-y-3">
        <h2 className="text-base font-semibold text-e0e3-foreground">Decisiones</h2>
        {errorCarga && (
          <p className="text-sm text-red-300">{errorCarga}</p>
        )}
        {!errorCarga && relaciones.length === 0 && (
          <p className="text-sm text-e0e3-muted">Todavía no hay relaciones en esta empresa.</p>
        )}
        {relaciones.map((relacion) => (
          <article key={relacion.id} className="rounded-2xl border border-white/10 bg-e0e3-surface p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="font-medium text-e0e3-foreground">
                  {relacion.codigo_externo} — {relacion.nombre_comercial}
                </p>
                <p className="text-sm text-e0e3-muted">
                  {nombreProveedor(relacion.proveedor_id)} · {ETIQUETA[relacion.estado] || relacion.estado}
                </p>
                <p className="text-sm text-e0e3-muted">
                  Producto: {nombreProducto(relacion.producto_id)} · Familia: {relacion.familia || '—'} · Calidad: {relacion.calidad || '—'}
                </p>
                <p className="text-sm text-e0e3-muted">
                  {relacion.usuario_nombre || '—'} · {relacion.fecha_decision ? String(relacion.fecha_decision).slice(0, 16).replace('T', ' ') : '—'}
                </p>
              </div>
            </div>
            {puedeDecidir && (
              <div className="mt-3 space-y-2">
                <select
                  className={campo}
                  value={relacion.producto_id || ''}
                  onChange={(event) => actualizarFila(relacion.id, { producto_id: event.target.value })}
                >
                  <option value="">Sin producto interno</option>
                  {productos.map((prod) => (
                    <option key={prod.id} value={prod.id}>{prod.nombre}</option>
                  ))}
                </select>
                <input
                  className={campo}
                  placeholder="Evidencia de esta decisión"
                  value={relacion.evidenciaBorrador ?? relacion.evidencia ?? ''}
                  onChange={(event) => actualizarFila(relacion.id, { evidenciaBorrador: event.target.value })}
                />
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" color="primary" disabled={guardando} onClick={() => decidir(relacion, 'confirmada')}>Confirmar</Button>
                  <Button type="button" size="sm" color="danger" disabled={guardando} onClick={() => decidir(relacion, 'rechazada')}>Rechazar</Button>
                  <Button type="button" size="sm" color="warning" disabled={guardando} onClick={() => decidir(relacion, 'misma_familia_distinto_producto')}>Otro producto</Button>
                  <Button type="button" size="sm" color="secondary" disabled={guardando} onClick={() => decidir(relacion, 'pendiente')}>Volver a pendiente</Button>
                </div>
              </div>
            )}
            {Array.isArray(relacion.historial) && relacion.historial.length > 0 && (
              <details className="mt-3 text-sm text-e0e3-muted">
                <summary>Quién decidió</summary>
                <ul className="mt-2 space-y-1">
                  {relacion.historial.map((item, index) => (
                    <li key={`${relacion.id}-${index}`}>
                      {item.usuario_nombre || 'usuario'} pasó de {item.estado_anterior || 'nueva'} a {ETIQUETA[item.estado_nuevo] || item.estado_nuevo}
                      {item.evidencia ? ` · ${item.evidencia}` : ''}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </article>
        ))}
      </section>
    </div>
  );
}
